import React, { useEffect, useState } from 'react';
import {
  Box,
  Chip,
  IconButton,
  TextField,
  Tooltip,
  CircularProgress,
  Typography,
} from '@mui/material';
import { Edit, Check, Close } from '@mui/icons-material';
import { format } from 'date-fns';
import { pl } from 'date-fns/locale';
import { toDateInputValue, getTodayDateInputValue } from '../models/Client';

const formatContactDate = (dateStr) => {
  if (!dateStr) return '—';
  try {
    return format(new Date(dateStr), 'dd MMM yyyy', { locale: pl });
  } catch {
    return dateStr;
  }
};

const LastContactHeaderField = ({
  manualDate,
  latestActivityDate,
  editingField,
  savingField,
  onStartEdit,
  onCancelEdit,
  onSave,
  editable,
}) => {
  const [draft, setDraft] = useState('');
  const [error, setError] = useState('');
  const isEditing = editingField === 'lastContactDate';
  const isSaving = savingField === 'lastContactDate';
  const effectiveDate = manualDate || latestActivityDate || null;
  const isFromActivity = !manualDate && Boolean(latestActivityDate);
  const editValue = toDateInputValue(manualDate) || toDateInputValue(latestActivityDate);
  const maxDate = getTodayDateInputValue();
  const initialDraft = editValue && editValue > maxDate ? maxDate : editValue;

  useEffect(() => {
    if (isEditing) {
      setDraft(initialDraft);
      setError('');
    }
  }, [isEditing, initialDraft]);

  const handleSave = async (e) => {
    e.stopPropagation();
    const result = await onSave('lastContactDate', draft);
    if (!result?.success) {
      setError(result?.error || 'Nie udało się zapisać daty.');
    }
  };

  const handleCancel = (e) => {
    e.stopPropagation();
    setDraft(initialDraft);
    setError('');
    onCancelEdit();
  };

  if (isEditing) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-end',
          gap: 0.5,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            flexWrap: 'wrap',
            gap: 0.5,
          }}
        >
          <TextField
            type="date"
            size="small"
            value={draft}
            onChange={(e) => {
              setDraft(e.target.value);
              setError('');
            }}
            error={Boolean(error)}
            inputProps={{ max: maxDate }}
            InputLabelProps={{ shrink: true }}
            sx={{ maxWidth: 160 }}
          />
          <Tooltip title="Zapisz">
            <span>
              <IconButton size="small" color="primary" onClick={handleSave} disabled={isSaving}>
                {isSaving ? <CircularProgress size={16} /> : <Check fontSize="small" />}
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Anuluj">
            <IconButton size="small" onClick={handleCancel} disabled={isSaving}>
              <Close fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
        <Typography variant="caption" color={error ? 'error' : 'text.secondary'}>
          {error || 'Maks. dzisiaj. Wyczyść pole → użyje daty z komentarza'}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25 }}>
      <Chip
        size="small"
        variant="outlined"
        label={
          <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
            <Typography component="span" variant="caption" color="text.secondary">
              Ostatni kontakt:
            </Typography>
            <Typography component="span" variant="caption" sx={{ fontWeight: 600 }}>
              {formatContactDate(effectiveDate)}
              {isFromActivity ? ' *' : ''}
            </Typography>
          </Box>
        }
        sx={{ height: 'auto', py: 0.25, '& .MuiChip-label': { px: 1 } }}
      />
      {editable && (
        <Tooltip title={isFromActivity ? 'Edytuj (obecnie z ostatniego komentarza)' : 'Edytuj datę kontaktu'}>
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onStartEdit('lastContactDate');
            }}
            disabled={Boolean(editingField) || isSaving}
            aria-label="Edytuj datę ostatniego kontaktu"
          >
            <Edit sx={{ fontSize: 15 }} />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );
};

export default LastContactHeaderField;
